import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/helpers';
import { FiEdit2, FiTrash2, FiGrid, FiUsers, FiHome, FiAlertCircle, FiCheckCircle, FiXCircle, FiGift, FiSlash, FiClipboard, FiEye } from 'react-icons/fi';
import clsx from 'clsx';
import AssignSubscriptionModal from './AssignSubscriptionModal';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import { useAuth } from '../../context/AuthContext';

const EstablishmentTable = ({ establishments, onEdit, onDelete, onRefresh }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignSubscriptionModal, setAssignSubscriptionModal] = useState({
    isOpen: false,
    establishment: null
  });
  const [cancelSubscriptionModal, setCancelSubscriptionModal] = useState({
    isOpen: false,
    establishment: null,
    subscription: null
  });

  const handleAssignSubscription = (establishment) => {
    setAssignSubscriptionModal({
      isOpen: true,
      establishment
    });
  };

  const handleCancelSubscription = (establishment) => {
    setCancelSubscriptionModal({
      isOpen: true,
      establishment,
      subscription: establishment.active_subscription
    });
  };

  const handleSubscriptionAssignSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleSubscriptionCancelSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleViewDetails = (e, establishment) => {
    e.stopPropagation();
    navigate(`/admin/establishments/${establishment.id}`);
  };

  const handleEditClick = (e, establishment) => {
    e.stopPropagation();
    onEdit(establishment);
  };

  const handleDeleteClick = (e, establishment) => {
    e.stopPropagation();
    onDelete(establishment);
  };

  const handleAssignSubscriptionClick = (e, establishment) => {
    e.stopPropagation();
    handleAssignSubscription(establishment);
  };

  const handleCancelSubscriptionClick = (e, establishment) => {
    e.stopPropagation();
    handleCancelSubscription(establishment);
  };

  const isPlatformAdmin = user?.role === 'admin_plataforma';
  const isSales = user?.role === 'ventas';
  const canManageSubscriptions = isPlatformAdmin || isSales;
  const canViewDetails = isPlatformAdmin; // Sales users cannot view establishment details
  if (establishments.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <FiGrid className="h-16 w-16 text-gray-300 mx-auto mb-4" aria-hidden="true" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No se encontraron establecimientos
        </h3>
        <p className="text-gray-500">
          No hay establecimientos que coincidan con los filtros actuales
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Establecimiento
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Habitaciones
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuarios
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Órdenes
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Incidencias
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Suscripción
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Creado
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {establishments.map((establishment) => (
              <tr key={establishment.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0">
                      <div className="h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <FiGrid className="h-5 w-5 text-white" aria-hidden="true" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {establishment.name}
                      </div>
                      {establishment.description && (
                        <div className="text-sm text-gray-500">
                          {establishment.description.length > 50
                            ? `${establishment.description.substring(0, 50)}...`
                            : establishment.description
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center">
                    <FiHome className="h-4 w-4 text-blue-500 mr-2" aria-hidden="true" />
                    <span className="text-sm font-medium text-gray-900">
                      {establishment.rooms_count || 0}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center">
                    <FiUsers className="h-4 w-4 text-green-500 mr-2" aria-hidden="true" />
                    <span className="text-sm font-medium text-gray-900">
                      {establishment.users_count || 0}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center">
                    <FiClipboard className="h-4 w-4 text-purple-500 mr-2" aria-hidden="true" />
                    <span className="text-sm font-medium text-gray-900">
                      {establishment.orders_count || 0}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center">
                    <FiAlertCircle className="h-4 w-4 text-orange-500 mr-2" aria-hidden="true" />
                    <span className="text-sm font-medium text-gray-900">
                      {establishment.incidents_count || 0}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {establishment.active_subscription ? (
                    <div className="flex items-center">
                      <FiCheckCircle className="h-4 w-4 text-green-500 mr-2" aria-hidden="true" />
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-gray-900 capitalize">
                          Plan {establishment.active_subscription.plan}
                        </span>
                        <span className={clsx('text-xs font-medium', {
                          'text-blue-600': establishment.active_subscription.status === 'trial',
                          'text-green-600': establishment.active_subscription.status === 'active'
                        })}>
                          {establishment.active_subscription.status === 'trial' ? 'Período de Prueba' : 'Activa'}
                        </span>
                        {establishment.active_subscription.trial_end && (
                          <span className="text-xs text-gray-500">
                            Trial hasta: {new Date(establishment.active_subscription.trial_end).toLocaleDateString('es-ES')}
                          </span>
                        )}
                        {establishment.active_subscription.current_period_end && !establishment.active_subscription.trial_end && (
                          <span className="text-xs text-gray-500">
                            Renueva: {new Date(establishment.active_subscription.current_period_end).toLocaleDateString('es-ES')}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600">
                      <FiXCircle className="h-4 w-4 mr-2" aria-hidden="true" />
                      <span className="text-xs font-medium">Sin suscripción</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={clsx(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    {
                      'bg-green-100 text-green-800': establishment.is_active,
                      'bg-red-100 text-red-800': !establishment.is_active,
                    }
                  )}>
                    {establishment.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(new Date(establishment.created_at))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    {canViewDetails && (
                      <button
                        onClick={(e) => handleViewDetails(e, establishment)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50"
                        aria-label="Ver detalles"
                        title="Ver detalles"
                      >
                        <FiEye className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                    {canManageSubscriptions && !establishment.active_subscription && (
                      <button
                        onClick={(e) => handleAssignSubscriptionClick(e, establishment)}
                        className="text-green-600 hover:text-green-900 p-1 rounded-md hover:bg-green-50"
                        aria-label="Asignar suscripción"
                        title="Asignar suscripción"
                      >
                        <FiGift className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                    {canManageSubscriptions && establishment.active_subscription && (
                      <button
                        onClick={(e) => handleCancelSubscriptionClick(e, establishment)}
                        className="text-orange-600 hover:text-orange-900 p-1 rounded-md hover:bg-orange-50"
                        aria-label="Cancelar suscripción"
                        title="Cancelar suscripción"
                      >
                        <FiSlash className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                    {(isPlatformAdmin || isSales) && (
                      <button
                        onClick={(e) => handleEditClick(e, establishment)}
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50"
                        aria-label="Editar establecimiento"
                        title="Editar establecimiento"
                      >
                        <FiEdit2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                    {isPlatformAdmin && (
                      <button
                        onClick={(e) => handleDeleteClick(e, establishment)}
                        className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                        aria-label="Eliminar establecimiento"
                        title="Eliminar establecimiento"
                      >
                        <FiTrash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AssignSubscriptionModal
        isOpen={assignSubscriptionModal.isOpen}
        onClose={() => setAssignSubscriptionModal({ isOpen: false, establishment: null })}
        establishment={assignSubscriptionModal.establishment}
        onSuccess={handleSubscriptionAssignSuccess}
      />

      <CancelSubscriptionModal
        isOpen={cancelSubscriptionModal.isOpen}
        onClose={() => setCancelSubscriptionModal({ isOpen: false, establishment: null, subscription: null })}
        establishment={cancelSubscriptionModal.establishment}
        subscription={cancelSubscriptionModal.subscription}
        onSuccess={handleSubscriptionCancelSuccess}
      />
    </div>
  );
};

export default EstablishmentTable;